/*
 * Free-text search over the spare parts register.
 *
 * Pure functions of their arguments so the rules can be tested without rendering a grid, in the
 * shape of sparePartPicker.js and stockMovements.js.
 *
 * Client-side, following features/activities/components/RecurringActivitiesGrid.jsx: the register
 * endpoint already hands over every row, so the search filters something we are holding rather
 * than asking the API a question it has answered.
 *
 * The matching rule moved to features/cmms/cmmsSearch.js when the work order list became its
 * second caller. What is searched is still decided here; only how is shared.
 */
import { filterBySearchText } from "../cmms/cmmsSearch";

/*
 * The row's seven searchable fields, lower-cased and joined.
 *
 * The activities pattern resolves ids to names before searching — machine name, tool name — and
 * that is the detail that decides whether a search box works. Here it costs nothing: every field
 * is text on the row itself, so there is nothing to look up. The work order list was not so
 * lucky, since a work order carries assetNodeId and a numeric status.
 *
 * The location came within a hair of being that problem. It is a foreign key on the server,
 * so the row would have arrived carrying storageLocationId — a number nobody searches for. It
 * reads storageLocationName instead because the server flattens the navigation onto the view
 * model, which is what keeps the resolve-before-searching step out of here.
 *
 * The type is the same foreign key in the same shape, flattened the same way, and it is here for a
 * reason the location does not have: its column is hidden by default, so searching is the main way
 * anyone finds the bearings among four hundred parts.
 *
 * The supplier's article number and name2 are the reason a register is searched at all: someone
 * holding a delivery note has the supplier's number and not ours, and before this the only way to
 * find that row was to already know what we call it. There is no ERP number beside it any more —
 * the part number is the ERP's number (§3.4 Q1), so searching one searches the other.
 *
 * Unit and unit cost are left out on purpose. Searching "st" would match half a Swedish register,
 * and nobody looks for a part by its price.
 *
 * Description is searched even though the grid has no column for it. It is where "deep groove
 * ball bearing" is written when the name is only "Bearing 6204" — which makes it the field most
 * worth searching and the one least worth a column. The supplier's number is the opposite case:
 * it has a column, hidden by default, and is worth searching either way.
 */
export const getSparePartSearchText = (part) =>
  [
    part?.partNumber,
    part?.supplierArticleNumber,
    part?.name,
    part?.name2,
    part?.description,
    part?.storageLocationName,
    part?.sparePartTypeName,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/*
 * The parts matching every word of the query, in any field and any order — so someone can
 * combine a word from the name with the shelf they think it is on. See cmmsSearch.js for why
 * that beats the single-substring match the activities grid uses.
 */
export const filterSpareParts = (spareParts, query) =>
  filterBySearchText(spareParts, query, getSparePartSearchText);
